import gsap from 'gsap'

/**
 * gsap helper functions
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */

/**
 * Adds an array of animations to a timeline with a stagger.
 * @param animations Array of animations
 * @param stagger Time in seconds to delay each subsequent animation
 * @param vars Variables to be passed to the timeline constructor
 * @returns Timeline
 */
export function staggerTimeline(animations: Array<GSAPAnimation>, stagger = 0, vars?: GSAPTimelineVars): GSAPTimeline {
    /** Reducer function to add animations in an array to a timeline */
    const reduceTimeline = (timeline: GSAPTimeline, animation: GSAPAnimation, i: number) => {
        return timeline.add(animation, i * stagger)
    }

    return animations.reduce<GSAPTimeline>(reduceTimeline, gsap.timeline(vars))
}

interface TimelineChildrenGroups {
    finished: GSAPAnimation[]
    active: GSAPAnimation[]
    upcoming: GSAPAnimation[]
}

export function groupTimelineChildrenByTime(timeline: GSAPTimeline, time: gsap.Position): TimelineChildrenGroups {
    // Create a temporary tween to get the start time
    const temporaryTween = gsap.set({}, {})
    timeline.add(temporaryTween, time)
    const timeToCompare = temporaryTween.startTime()

    // Remove the tween from the timeline
    temporaryTween.kill()
    timeline.remove(temporaryTween)

    const children = timeline.getChildren(false)
    const output: TimelineChildrenGroups = {
        finished: [],
        active: [],
        upcoming: [],
    }

    for (const child of children) {
        const startTime = child.startTime()
        const duration = child.totalDuration()
        const endTime = startTime + duration

        if (endTime < timeToCompare) {
            output.finished.push(child)
        } else if (startTime <= timeToCompare) {
            output.active.push(child)
        } else {
            output.upcoming.push(child)
        }
    }

    return output
}
